/* DoughRise entitlement resolver
 *
 * Shopify customer tags are set by Appstle when a subscription is created, so
 * they only ever describe PAID subscriptions. They cannot represent:
 *
 *   - an active 14-day Pro reverse trial (plan stays 'free' in D1)
 *   - comped access granted in D1 (comp_tier, no Shopify subscription)
 *   - an entitlement change that has not yet propagated back to a tag
 *
 * The API already resolves all of these in one place, entitledTier(), and
 * returns the answer as `entitlement` on GET /auth/me. This module asks for it
 * and upgrades window.__drIsPro when the answer is better than the tags knew.
 *
 * Deliberately one-way: it can only ever GRANT access, never revoke it. A
 * failed request, an offline visitor or a slow API leaves the tag-derived value
 * exactly as the server rendered it. Nothing here is a security control —
 * entitlement is enforced by the API on every gated call. This only decides
 * which UI a legitimate user is shown.
 */
(function () {
  'use strict';

  var API = 'https://api.doughrise.store/auth/me';
  var PAID = { pro: 1, coach: 1, bakery: 1 };

  function hasSessionCookie() {
    // dr_auth is a non-HttpOnly companion to the HttpOnly __session cookie, set
    // on the same domain (.doughrise.store) for exactly this purpose. Checking
    // it first means a logged-out visitor never makes a doomed 401 request.
    return document.cookie.indexOf('dr_auth=1') !== -1;
  }

  // Deliberately NOT cached across page loads.
  //
  // An earlier version kept the answer in sessionStorage for five minutes. That
  // cannot be made correct with what the storefront can read: __session is
  // HttpOnly by design, and every other readable cookie is either identical
  // between two accounts (dr_auth=1) or empty for both (dr_trial_until, for any
  // two non-trial users). Two accounts used in the same tab therefore share a
  // key, and the second inherits the first's entitlement. Clearing on logout
  // does not close it either: a magic-link callback signs the next user in
  // without ever loading a logged-out storefront page.
  //
  // The cache only ever saved one request on a warm session. Correctness is
  // worth more than that, so the answer is resolved per page load and held in
  // memory only, where it cannot outlive the session it belongs to.

  function apply(entitlement) {
    if (!entitlement || !PAID[entitlement]) return false;
    var changed = window.__drIsPro !== true;
    window.__drIsPro = true;
    window.__drEntitlement = entitlement;
    if (changed) {
      // Sections that rendered an upsell against the tag-derived value listen
      // for this and re-render. Fired only on an actual upgrade, so nothing
      // re-runs on the common path where the tags were already correct.
      document.dispatchEvent(
        new CustomEvent('dr:entitlement', { detail: { entitlement: entitlement } })
      );
    }
    return changed;
  }

  function resolve() {
    if (!hasSessionCookie()) return;

    fetch(API, { credentials: 'include', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (me) {
        if (!me || !me.entitlement) return;
        apply(me.entitlement);
      })
      .catch(function () {});   // fail closed to the tag value; never block the page
  }

  // Expose for callers that need certainty at a decision point rather than
  // whatever the ambient flag happens to be (e.g. before firing a gate event).
  window.drEntitlement = function () {
    return window.__drEntitlement || (window.__drIsPro ? 'pro' : 'free');
  };

  resolve();
})();
